import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

/* --------------------------------------------------------------------------
   Render outside `.screen`.

   `.screen` is `position: relative; z-index: 1`, which makes it a stacking
   context — so anything `position: fixed` declared inside a screen is capped
   at 1 and paints *under* the app header and tab bar (z-index 30 and 50 at the
   root), however high its own z-index goes. No z-index on the overlay can win;
   the whole subtree is under the cap.

   Escaping to `document.body` is the fix, and it is why <Sheet> renders through
   this. Reach for it directly only for a full-screen overlay that is not a
   Sheet — the receipt camera, for instance.

   Three screens each wrote this file for themselves before it was lifted here.
   ----------------------------------------------------------------------- */

export function Portal({ children, name }: { children: ReactNode; name?: string }) {
  const [host] = useState<HTMLDivElement | null>(() =>
    typeof document === 'undefined' ? null : document.createElement('div'));

  useEffect(() => {
    if (!host) return;
    host.setAttribute('data-portal', name ?? 'overlay');
    document.body.appendChild(host);
    return () => { host.remove(); };
  }, [host, name]);

  if (!host) return null;
  return createPortal(children, host);
}
